import { TestLocators } from 'constants/Locators';

import { MUI, ThemeMode } from 'ci-esi-web-shared';
import React, { PropsWithChildren, useCallback } from 'react';

import { ButtonDownListener } from 'app/components/common/ButtonDownListener';
import { colors } from 'styles/theme/colors';

export interface CustomHeaderMenuProps extends PropsWithChildren<MUI.MenuProps> {
  paperTestId?: TestLocators | string;
  menuListTestId?: TestLocators | string;
  popperPlacement?: MUI.PopperPlacementType;
}

export function CustomHeaderMenu({
  open,
  children,
  anchorEl,
  onClose,
  paperTestId,
  menuListTestId,
  popperPlacement = 'bottom-end',
}: CustomHeaderMenuProps) {
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const boxShadow = isDarkMode ? '0 2px 5px 2px rgba(0,0,0,0.5)' : '0 2px 3px 1px rgba(0,0,0,0.2)';

  const handleClose = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('MuiListItem-root') || target === document.body) {
        return;
      }
      if (onClose) {
        onClose(event as unknown as React.MouseEvent<Document>, 'backdropClick');
      }
    },
    [onClose],
  );

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        event.preventDefault();
        onClose(event, 'escapeKeyDown');
      }
    },
    [onClose],
  );

  return (
    <MUI.Popper
      sx={{ zIndex: 11, marginTop: '8px', marginRight: '10px' }}
      open={open}
      anchorEl={anchorEl as Element}
      transition
      disablePortal
      placement={popperPlacement}
      data-testid={`${TestLocators.Menu_Popper}_${open ? 'Open' : 'Closed'}`}
    >
      {({ TransitionProps, placement }) => (
        <MUI.Grow
          {...TransitionProps}
          style={{ transformOrigin: placement.includes('bottom') ? 'center top' : 'center bottom' }}
        >
          <MUI.Paper
            data-testid={paperTestId}
            sx={{
              background: isDarkMode ? colors.grey55 : colors.primaryWhite,
              boxShadow,
              border: '1px solid',
              borderColor: 'text.secondary',
              color: 'text.secondary',
              top: '50px!important',
            }}
          >
            <ButtonDownListener handler={handleListKeyDown}>
              <MUI.ClickAwayListener onClickAway={handleClose}>
                <MUI.MenuList sx={{ padding: 0 }} data-testid={menuListTestId}>
                  {children}
                </MUI.MenuList>
              </MUI.ClickAwayListener>
            </ButtonDownListener>
          </MUI.Paper>
        </MUI.Grow>
      )}
    </MUI.Popper>
  );
}
